import React, { useState, useEffect } from 'react';
import { Signatory } from '../types';
import { X, Save, UserCheck, AlertCircle, CheckCircle2 } from 'lucide-react';

interface SignatoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  signatories: Signatory[];
  onSave: (signatories: Signatory[]) => void;
}

export const SignatoryModal: React.FC<SignatoryModalProps> = ({
  isOpen,
  onClose,
  signatories,
  onSave,
}) => {
  const [formData, setFormData] = useState<Signatory[]>([]);
  const [savedSuccess, setSavedSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Ensure 4 entries: ผู้จัดทำ, ผู้รายงาน, ผู้ตรวจสอบ, ผู้อนุมัติ
      const defaultRoles = ['ผู้จัดทำ', 'ผู้รายงาน', 'ผู้ตรวจสอบ', 'ผู้อนุมัติ'];
      const initial: Signatory[] = [0, 1, 2, 3].map((idx) => ({
        id: signatories[idx]?.id || `sig_${idx + 1}`,
        roleTitle: (signatories[idx]?.roleTitle && signatories[idx]?.roleTitle !== 'ผู้จัดทำ / ผู้รายงาน')
          ? signatories[idx].roleTitle
          : defaultRoles[idx],
        name: signatories[idx]?.name || '',
        position: signatories[idx]?.position || '',
      }));
      setFormData(initial);
      setSavedSuccess(false);

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, signatories, onClose]);

  if (!isOpen) return null;

  const handleChange = (idx: number, field: keyof Signatory, value: string) => {
    const updated = [...formData];
    updated[idx] = {
      ...updated[idx],
      [field]: value,
    };
    setFormData(updated);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    setSavedSuccess(true);
    setTimeout(() => {
      setSavedSuccess(false);
      onClose();
    }, 800);
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-fade-in"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50/50">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
              <UserCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800">ตั้งค่าผู้ลงนามท้ายรายงาน (4 คน)</h3>
              <p className="text-xs text-slate-500">ระบุชื่อและตำแหน่งที่จะแสดงในเอกสาร Word / PDF และการพิมพ์</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Form */}
        <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-6 space-y-5">
          {savedSuccess && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-emerald-800 text-sm font-medium">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <span>บันทึกข้อมูลผู้ลงนามเรียบร้อยแล้ว</span>
            </div>
          )}

          <div className="space-y-4">
            {formData.map((sig, idx) => (
              <div
                key={sig.id}
                className="p-4 rounded-xl border border-slate-200 bg-slate-50/40 hover:bg-slate-50 transition-colors space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-md border border-blue-200">
                    ผู้ลงนามคนที่ {idx + 1}
                  </span>
                  <div className="w-48">
                    <input
                      type="text"
                      value={sig.roleTitle}
                      onChange={(e) => handleChange(idx, 'roleTitle', e.target.value)}
                      placeholder="บทบาท เช่น ผู้รายงาน"
                      className="w-full text-xs font-medium text-right text-slate-600 bg-transparent border-b border-dashed border-slate-300 focus:border-blue-500 focus:outline-none px-1 py-0.5"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      ชื่อ - นามสกุล
                    </label>
                    <input
                      type="text"
                      value={sig.name}
                      onChange={(e) => handleChange(idx, 'name', e.target.value)}
                      placeholder="เช่น นายสมชาย ใจดี"
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-slate-400"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      ตำแหน่ง
                    </label>
                    <input
                      type="text"
                      value={sig.position}
                      onChange={(e) => handleChange(idx, 'position', e.target.value)}
                      placeholder="เช่น นายช่างเครื่องกล 6"
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-slate-400"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-start gap-2 p-3 bg-amber-50/70 border border-amber-200/80 rounded-xl text-amber-800 text-xs">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <span>
              ข้อมูลผู้ลงนามจะถูกบันทึกไว้ในเบราว์เซอร์ของคุณโดยอัตโนมัติ และจะถูกนำไปใช้ในเอกสาร Word และ PDF ทุกครั้งที่กดสั่งพิมพ์หรือดาวน์โหลด
            </span>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-xs"
            >
              <Save className="w-4 h-4" />
              <span>บันทึกข้อมูล</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
